import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils/classNames';

/**
 * GGC V2. Badge — Figma node 131:411 family (Component 01).
 *
 * Pill-shaped status indicator with a colored dot and label. Each status
 * uses a `ggc-*` color pair (`-light` background + base text/dot).
 *
 * Use this when displaying campaign/influencer/system status. For non-status
 * decorative badges, prefer the legacy `@/components/ui/badge`.
 */
const ggcBadgeVariants = cva(
  'inline-flex items-center gap-[5px] rounded-ggc-pill px-[11px] py-[4px] ggc-text-micro-bold font-ggc whitespace-nowrap',
  {
    variants: {
      status: {
        draft: 'bg-ggc-orange-light text-ggc-orange',
        'in-progress': 'bg-ggc-blue-light text-ggc-blue',
        completed: 'bg-ggc-green-light text-ggc-green',
        'private-draft': 'bg-ggc-purple-light text-ggc-purple',
        template: 'bg-ggc-sub-yellow-light text-ggc-sub-yellow',
        roster: 'bg-ggc-sub-skyblue-light text-ggc-sub-skyblue',
        success: 'bg-ggc-green-light text-ggc-green',
        warning: 'bg-ggc-orange-light text-ggc-orange',
        error: 'bg-ggc-red-light text-ggc-red',
        info: 'bg-ggc-sub-olive-light text-ggc-sub-olive',
        aborted: 'bg-ggc-sub-pink-light text-ggc-sub-pink',
        active: 'bg-ggc-guide-blue-bg text-ggc-cta-blue',
        /** Gray/muted — for canceled, aborted, or any inactive status */
        muted: 'bg-ggc-bg-base text-ggc-text-gray border border-ggc-border-gray',
      },
    },
    defaultVariants: {
      status: 'draft',
    },
  }
);

/**
 * `draft`/`in-progress`/`private-draft` also get `ggc-status-dot--live` (blink
 * animation, defined in components.css) — mirrors CAMPAIGN_STATUS_DOT_CLASS in
 * campaignStatusStyle.ts (see that file for why these three specifically).
 */
const dotColorMap: Record<NonNullable<GgcBadgeProps['status']>, string> = {
  draft: 'bg-ggc-orange ggc-status-dot--live',
  'in-progress': 'bg-ggc-blue ggc-status-dot--live',
  completed: 'bg-ggc-green',
  'private-draft': 'bg-ggc-purple ggc-status-dot--live',
  template: 'bg-ggc-sub-yellow',
  roster: 'bg-ggc-sub-skyblue',
  success: 'bg-ggc-green',
  warning: 'bg-ggc-orange',
  error: 'bg-ggc-red',
  info: 'bg-ggc-sub-olive',
  aborted: 'bg-ggc-sub-pink',
  active: 'bg-ggc-cta-blue',
  muted: 'bg-ggc-disabled',
};

/** Dot/accent background token per status — for spots that need only the color (a status
 *  dot, a tinted icon) rather than a whole badge, so they stay on the same palette as
 *  `GgcBadge` instead of hard-coding hex. */
export const ggcStatusDotClass: Record<NonNullable<GgcBadgeProps['status']>, string> = dotColorMap;

/** Text-color token per status, matching each `GgcBadge` tone. */
export const ggcStatusTextClass: Record<NonNullable<GgcBadgeProps['status']>, string> = {
  draft: 'text-ggc-orange',
  'in-progress': 'text-ggc-blue',
  completed: 'text-ggc-green',
  'private-draft': 'text-ggc-purple',
  template: 'text-ggc-sub-yellow',
  roster: 'text-ggc-sub-skyblue',
  success: 'text-ggc-green',
  warning: 'text-ggc-orange',
  error: 'text-ggc-red',
  info: 'text-ggc-sub-olive',
  aborted: 'text-ggc-sub-pink',
  active: 'text-ggc-cta-blue',
  muted: 'text-ggc-text-gray',
};

const labelMap: Record<NonNullable<GgcBadgeProps['status']>, string> = {
  draft: 'Draft',
  'in-progress': 'In Progress',
  completed: 'Completed',
  'private-draft': 'Private Draft',
  template: 'Template',
  roster: 'Roster',
  success: 'Success',
  warning: 'Warning',
  error: 'Error',
  info: 'Info',
  aborted: 'Aborted',
  active: 'Active',
  muted: 'Muted',
};

export interface GgcBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof ggcBadgeVariants> {
  /** Hide the leading dot when only the label is needed. */
  hideDot?: boolean;
  /** Override the default English label (e.g. for i18n or custom statuses). */
  children?: React.ReactNode;
  /**
   * Optional count appended after the label with a separator: "Completed | 3".
   * Matches Figma "Status13" variant (node 325:55).
   */
  count?: number;
}

const GgcBadge = React.forwardRef<HTMLSpanElement, GgcBadgeProps>(
  ({ className, status, hideDot, count, children, ...props }, ref) => {
    const resolvedStatus = status ?? 'draft';
    return (
      <span ref={ref} className={cn(ggcBadgeVariants({ status: resolvedStatus }), className)} {...props}>
        {!hideDot && (
          <span
            aria-hidden
            className={cn('size-[5px] rounded-full', dotColorMap[resolvedStatus])}
          />
        )}
        {children ?? labelMap[resolvedStatus]}
        {count != null && (
          <span aria-hidden className="opacity-40 mx-[2px]">|</span>
        )}
        {count != null && <span>{count}</span>}
      </span>
    );
  }
);
GgcBadge.displayName = 'GgcBadge';

export { GgcBadge, ggcBadgeVariants };
